import { Download, FileSpreadsheet } from 'lucide-react'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { getApiErrorMessage } from '@/lib/api-error'
import { downloadCsv, downloadXlsx, exportFilename, type ExportField } from '@/lib/export'
import { formatNumber } from '@/lib/money'

export interface ExportDialogProps<TRow> {
    open: boolean
    onOpenChange: (open: boolean) => void
    /** Filename stem and sheet name, e.g. `'deposits'`. */
    name: string
    fields: ExportField<TRow>[]
    /**
     * Fetches every row for the current filters, not just the visible page --
     * exporting page 3 of a filtered list is almost never what was meant.
     * `onProgress` reports rows fetched so far for long paginated pulls.
     */
    fetchRows: (onProgress: (fetched: number) => void) => Promise<TRow[]>
    /** Shown in the dialog so the operator knows the filters are in effect. */
    scopeLabel?: string
}

export function ExportDialog<TRow>({
    open,
    onOpenChange,
    name,
    fields,
    fetchRows,
    scopeLabel
}: ExportDialogProps<TRow>) {
    const [selected, setSelected] = useState<Set<string>>(
        () => new Set(fields.filter(field => !field.optional).map(field => field.key))
    )
    const [busy, setBusy] = useState(false)
    const [fetched, setFetched] = useState(0)

    const chosen = useMemo(() => fields.filter(field => selected.has(field.key)), [fields, selected])

    const toggle = (key: string) => {
        setSelected(current => {
            const next = new Set(current)
            if (next.has(key)) next.delete(key)
            else next.add(key)
            return next
        })
    }

    const run = async (format: 'csv' | 'xlsx') => {
        setBusy(true)
        setFetched(0)
        try {
            const rows = await fetchRows(setFetched)
            if (rows.length === 0) {
                toast.warning('Nothing to export', { description: 'The current filters match no records.' })
                return
            }

            const filename = exportFilename(name, format)
            if (format === 'csv') downloadCsv(rows, chosen, filename)
            else await downloadXlsx(rows, chosen, filename, name)

            toast.success(`Exported ${formatNumber(rows.length)} rows`, { description: filename })
            onOpenChange(false)
        } catch (error) {
            toast.error('Export failed', { description: getApiErrorMessage(error, 'The export could not be built.') })
        } finally {
            setBusy(false)
        }
    }

    return (
        <Dialog
            open={open}
            onOpenChange={value => {
                if (busy) return
                onOpenChange(value)
            }}>
            <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle>Export {name}</DialogTitle>
                    <DialogDescription>
                        {scopeLabel ?? 'Exports every row matching the current filters, not just this page.'}
                    </DialogDescription>
                </DialogHeader>

                <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-muted-foreground">
                            Columns ({chosen.length} of {fields.length})
                        </span>
                        {/* Adjacent, so both grow and the gap grows with them: at 360px the
                            label plus 46px + 8px + 72px still sits inside the dialog. */}
                        <div className="flex gap-1 pointer-coarse:gap-2">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setSelected(new Set(fields.map(field => field.key)))}
                                className="pointer-coarse:h-9">
                                All
                            </Button>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                    setSelected(new Set(fields.filter(f => !f.optional).map(field => field.key)))
                                }
                                className="pointer-coarse:h-9">
                                Default
                            </Button>
                        </div>
                    </div>

                    <ScrollArea className="max-h-64 rounded-lg border border-border">
                        <div className="flex flex-col gap-0.5 p-1.5">
                            {fields.map(field => (
                                <Label
                                    key={field.key}
                                    className="flex cursor-pointer items-start gap-2 rounded-md px-2 py-1.5 font-normal hover:bg-surface-raised">
                                    <Checkbox
                                        checked={selected.has(field.key)}
                                        onCheckedChange={() => toggle(field.key)}
                                        className="mt-0.5"
                                    />
                                    <span className="min-w-0">
                                        <span className="block text-sm">{field.label}</span>
                                        {field.hint && (
                                            <span className="block text-xs text-muted-foreground">{field.hint}</span>
                                        )}
                                    </span>
                                </Label>
                            ))}
                        </div>
                    </ScrollArea>
                </div>

                {busy && fetched > 0 && (
                    <>
                        <Separator />
                        <p aria-live="polite" className="tabular text-xs text-muted-foreground">
                            Fetched {formatNumber(fetched)} rows&hellip;
                        </p>
                    </>
                )}

                {/* The footer stacks below `sm` with 8px between two full-width buttons,
                    one of which starts a long download: a cluster, so they grow. */}
                <DialogFooter>
                    <Button
                        variant="outline"
                        loading={busy}
                        disabled={chosen.length === 0}
                        onClick={() => void run('xlsx')}
                        className="pointer-coarse:h-10">
                        <FileSpreadsheet />
                        Excel
                    </Button>
                    <Button
                        loading={busy}
                        disabled={chosen.length === 0}
                        onClick={() => void run('csv')}
                        className="pointer-coarse:h-10">
                        <Download />
                        CSV
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
